import { useCallback, useEffect, useState } from "react";
import { plannerAPI } from "../../../api/client";
import { expandDayRanges } from "../../../utils/dateUtils";
import { groupShiftRanges, monthDates, toDateStr } from "./shiftUtils";

function draftKey(year, month) {
  return `roster-grid/draft/${year}-${month}`;
}

function loadDraft(year, month) {
  try {
    const raw = localStorage.getItem(draftKey(year, month));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveDraft(year, month, draft) {
  try {
    if (draft && Object.keys(draft).length > 0) {
      localStorage.setItem(draftKey(year, month), JSON.stringify(draft));
    } else {
      localStorage.removeItem(draftKey(year, month));
    }
  } catch {}
}

const DEFAULT_WORKING_DAYS = ["MON", "TUE", "WED", "THU", "FRI"];

export function useRosterDraft(year, month, shiftTemplates, orgWorkingDays) {
  const [draft, setDraftState] = useState(() => loadDraft(year, month));
  const [isDraftMode, setIsDraftMode] = useState(() => !!loadDraft(year, month));
  const [isCopying, setIsCopying] = useState(false);
  const [applyProgress, setApplyProgress] = useState(0);
  const [isApplying, setIsApplying] = useState(false);

  // Re-load when month changes
  useEffect(() => {
    const saved = loadDraft(year, month);
    setDraftState(saved);
    setIsDraftMode(!!saved);
  }, [year, month]);

  const setDraft = useCallback(
    (next) => {
      setDraftState(next);
      saveDraft(year, month, next);
    },
    [year, month],
  );

  const copyLastMonth = useCallback(async () => {
    setIsCopying(true);
    try {
      const lastDate = new Date(year, month - 2, 1);
      const lastYear = lastDate.getFullYear();
      const lastMonth = lastDate.getMonth() + 1;

      const res = await plannerAPI.getTeam({ year: lastYear, month: lastMonth });
      const lastData = res.data;
      if (lastData?.users) {
        lastData.users = lastData.users.map((u) => ({
          ...u,
          days: expandDayRanges(u.dayRanges),
        }));
      }

      const newDraft = {};
      const targetDates = monthDates(year, month);

      for (const user of lastData?.users || []) {
        const userDraft = {};

        for (const targetStr of targetDates) {
          // Overlay: day N of current month uses day N of last month
          const dayNum = parseInt(targetStr.split("-")[2], 10);
          const lastStr = `${lastYear}-${String(lastMonth).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
          const lastDay = user.days?.[lastStr];

          if (!lastDay || lastDay.type !== "WORKING") continue;
          if (lastDay.scheduleSource !== "USER") continue;
          if (!lastDay.workStartTime || !lastDay.workEndTime) continue;
          const shift = shiftTemplates.find(
            (s) => s.workStartTime === lastDay.workStartTime && s.workEndTime === lastDay.workEndTime,
          );
          if (!shift) continue;

          userDraft[targetStr] = { type: "shift", shiftId: shift.id, shift };
        }

        if (Object.keys(userDraft).length > 0) {
          newDraft[user.userId] = userDraft;
        }
      }

      const hasData = Object.keys(newDraft).length > 0;
      setDraft(hasData ? newDraft : null);
      setIsDraftMode(hasData);
    } catch (err) {
      console.error("Copy last month failed:", err);
    } finally {
      setIsCopying(false);
    }
  }, [year, month, shiftTemplates, setDraft]);

  const applyDraft = useCallback(
    async (onDone) => {
      if (!draft) return;
      setIsApplying(true);
      setApplyProgress(0);

      const allEntries = [];
      for (const [userId, userDraft] of Object.entries(draft)) {
        const ranges = groupShiftRanges(userDraft);
        ranges.forEach((r) => allEntries.push({ userId, ...r }));
      }

      const total = allEntries.length;
      let done = 0;

      const workingDays = Array.isArray(orgWorkingDays) && orgWorkingDays.length
        ? orgWorkingDays
        : DEFAULT_WORKING_DAYS;

      for (const entry of allEntries) {
        try {
          await plannerAPI.updateUserSchedule(entry.userId, {
            workingDays,
            workStartTime: entry.shift.workStartTime,
            workEndTime: entry.shift.workEndTime,
            effectiveFrom: entry.start,
            effectiveTo: entry.end,
          });
          // Clear overrides so they don't mask the applied shift
          const end = new Date(entry.end);
          let d = new Date(entry.start);
          while (d <= end) {
            const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            try { await plannerAPI.removeDayOverride(entry.userId, dateStr); } catch {}
            d.setDate(d.getDate() + 1);
          }
        } catch {}
        done++;
        setApplyProgress(Math.round((done / total) * 100));
      }

      setDraft(null);
      setIsDraftMode(false);
      setIsApplying(false);
      onDone?.();
    },
    [draft, setDraft, orgWorkingDays],
  );

  const discardDraft = useCallback(() => {
    setDraft(null);
    setIsDraftMode(false);
  }, [setDraft]);

  const setCellDraft = useCallback(
    (userId, dateStr, cellData) => {
      setDraft((prev) => {
        const next = { ...(prev || {}) };
        if (cellData === null) {
          if (next[userId]) {
            const { [dateStr]: _removed, ...rest } = next[userId];
            next[userId] = rest;
            if (Object.keys(next[userId]).length === 0) delete next[userId];
          }
        } else {
          next[userId] = { ...(next[userId] || {}), [dateStr]: cellData };
        }
        return Object.keys(next).length === 0 ? null : next;
      });
    },
    [setDraft],
  );

  const getCellDraft = useCallback(
    (userId, dateStr) => draft?.[userId]?.[dateStr] || null,
    [draft],
  );

  return {
    draft,
    isDraftMode,
    isCopying,
    isApplying,
    applyProgress,
    copyLastMonth,
    applyDraft,
    discardDraft,
    setCellDraft,
    getCellDraft,
  };
}
